/**
 * SearchSelect —— 支持关键字搜索的底部选择弹层
 *
 * 适用于供应商、客户、工程等数据量较大、Vant Picker 滚轮不好找的场景。
 * - 默认本地过滤（数据已由接口一次性返回）
 * - 传入 remoteMethod 后自动切换为服务端搜索 + 滚动分页
 */
export default {
  name: 'SearchSelect',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '请选择'
    },
    // 原始列表，元素为 { text, value } 或任意对象（会自动取 name/Name/title 等字段）
    list: {
      type: Array,
      default: () => []
    },
    // 当前选中项的 value，用于打勾
    modelValue: {
      type: [String, Number],
      default: ''
    },
    // 自定义字段名
    fieldNames: {
      type: Object,
      default: () => ({
        text: 'text',
        value: 'value'
      })
    },
    placeholder: {
      type: String,
      default: '输入关键字搜索'
    },
    pageSize: {
      type: Number,
      default: 30
    },
    // 服务端搜索：(keyword, page) => Promise<Array>
    remoteMethod: {
      type: Function,
      default: null
    },
    debounce: {
      type: Number,
      default: 300
    }
  },
  emits: ['update:show', 'confirm'],
  data() {
    return {
      keyword: '',
      page: 1,
      loading: false,
      remoteList: [],
      remoteDone: false,
      timer: null
    };
  },
  computed: {
    innerShow: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit('update:show', val);
      }
    },
    // 把各种形态的原始数据统一成 { text, value, raw }
    normalList() {
      const t = this.fieldNames.text;
      const v = this.fieldNames.value;
      return this.list.map((raw, index) => {
        const text = raw[t] ?? raw.name ?? raw.Name ?? raw.title ?? raw.shortName ?? raw.ShortName ?? raw.ProjectShortName ?? '';
        const value = raw[v] ?? raw.id ?? raw.ID ?? raw.text ?? index;
        return {
          text: String(text),
          value,
          raw
        };
      });
    },
    isRemote() {
      return typeof this.remoteMethod === 'function';
    },
    matchList() {
      if (this.isRemote) {
        return this.remoteList.map((raw, index) => {
          const t = this.fieldNames.text;
          const v = this.fieldNames.value;
          const text = raw[t] ?? raw.name ?? raw.Name ?? raw.title ?? '';
          const value = raw[v] ?? raw.id ?? raw.ID ?? index;
          return {
            text: String(text),
            value,
            raw
          };
        });
      }
      const kw = this.keyword.trim().toLowerCase();
      if (!kw) return this.normalList;
      // 支持空格分词：「广东 钢」要求同时命中
      const words = kw.split(/\s+/);
      return this.normalList.filter(item => {
        const text = item.text.toLowerCase();
        return words.every(w => text.indexOf(w) > -1);
      });
    },
    pageList() {
      return this.matchList.slice(0, this.page * this.pageSize);
    }
  },
  methods: {
    onOpen() {
      this.page = 1;
      if (this.isRemote) {
        this.remoteList = [];
        this.remoteDone = false;
        this.fetchRemote();
      }
      this.$nextTick(() => {
        if (this.$refs.bodyEl) this.$refs.bodyEl.scrollTop = 0;
      });
    },
    onClose() {
      this.$emit('update:show', false);
    },
    onKeywordInput(val) {
      this.keyword = val;
      this.page = 1;
      clearTimeout(this.timer);
      if (!this.isRemote) return;
      this.timer = setTimeout(() => {
        this.remoteList = [];
        this.remoteDone = false;
        this.fetchRemote();
      }, this.debounce);
    },
    onSearch() {
      clearTimeout(this.timer);
      this.page = 1;
      if (this.isRemote) {
        this.remoteList = [];
        this.remoteDone = false;
        this.fetchRemote();
      }
    },
    onScroll(e) {
      const el = e.target;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 40) return;
      if (this.loading) return;
      if (this.isRemote) {
        if (this.remoteDone) return;
        this.page++;
        this.fetchRemote();
      } else if (this.pageList.length < this.matchList.length) {
        this.page++;
      }
    },
    async fetchRemote() {
      if (this.loading) return;
      this.loading = true;
      try {
        const rows = await this.remoteMethod(this.keyword.trim(), this.page);
        const arr = Array.isArray(rows) ? rows : [];
        if (this.page === 1) this.remoteList = arr;else this.remoteList = this.remoteList.concat(arr);
        if (arr.length < this.pageSize) this.remoteDone = true;
      } catch (err) {
        console.log('搜索失败', err);
        this.remoteDone = true;
      } finally {
        this.loading = false;
      }
    },
    isActive(item) {
      return String(item.value) === String(this.modelValue) || !this.modelValue && item.text === this.modelValue;
    },
    onPick(item) {
      this.$emit('confirm', item);
      this.$emit('update:show', false);
    },
    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, c => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[c]);
    },
    // 关键字高亮
    highlight(text) {
      const safe = this.escapeHtml(text);
      const kw = this.keyword.trim();
      if (!kw) return safe;
      const words = kw.split(/\s+/).filter(Boolean).map(this.escapeHtml);
      if (!words.length) return safe;
      const reg = new RegExp('(' + words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')', 'gi');
      return safe.replace(reg, '<span class="ss-hl">$1</span>');
    }
  }
};